import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, Check, User, Users, GraduationCap, ShieldCheck } from 'lucide-react';
import { Account, AccountRole } from '../../types/accounts';
import { Badge } from '../ui/Badge';
interface AccountSwitcherDropdownProps {
  accounts: Account[];
  activeAccountId: string;
  onSwitchAccount: (accountId: string) => void;
}
function getAccountIcon(role: AccountRole) {
  switch (role) {
    case 'learner':
      return User;
    case 'parent':
      return Users;
    case 'teacher':
      return GraduationCap;
    case 'admin':
      return ShieldCheck;
  }
}
function getAccountRoleLabel(role: AccountRole) {
  switch (role) {
    case 'learner':
      return 'Leerling';
    case 'parent':
      return 'Ouder';
    case 'teacher':
      return 'Docent';
    case 'admin':
      return 'Beheerder';
  }
}
function getAccountColor(role: AccountRole) {
  switch (role) {
    case 'learner':
      return {
        bg: 'bg-indigo-100',
        text: 'text-indigo-700',
        border: 'border-indigo-200'
      };
    case 'parent':
      return {
        bg: 'bg-emerald-100',
        text: 'text-emerald-700',
        border: 'border-emerald-200'
      };
    case 'teacher':
      return {
        bg: 'bg-violet-100',
        text: 'text-violet-700',
        border: 'border-violet-200'
      };
    case 'admin':
      return {
        bg: 'bg-amber-100',
        text: 'text-amber-700',
        border: 'border-amber-200'
      };
  }
}
export function AccountSwitcherDropdown({
  accounts,
  activeAccountId,
  onSwitchAccount
}: AccountSwitcherDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const activeAccount = accounts.find(a => a.id === activeAccountId);
  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isOpen]);
  if (!activeAccount) return null;
  const Icon = getAccountIcon(activeAccount.role);
  const colors = getAccountColor(activeAccount.role);
  return <div className="relative" ref={dropdownRef} data-id="element-3848">
      {/* Trigger Button */}
      <button onClick={() => setIsOpen(!isOpen)} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white border border-slate-200 hover:border-slate-300 hover:shadow-sm transition-all" data-id="element-3849">
        {/* Avatar */}
        <div className={`w-8 h-8 rounded-full ${colors.bg} flex items-center justify-center`} data-id="element-3850">
          <Icon className={`w-4 h-4 ${colors.text}`} strokeWidth={2.5} data-id="element-3851" />
        </div>

        {/* Name & Role (hidden on mobile) */}
        <div className="hidden md:block text-left" data-id="element-3852">
          <div className="text-sm font-bold text-slate-900 leading-tight" data-id="element-3853">
            {activeAccount.displayName}
          </div>
          <div className="text-xs text-slate-500" data-id="element-3854">
            {getAccountRoleLabel(activeAccount.role)}
          </div>
        </div>

        {/* Chevron */}
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} data-id="element-3855" />
      </button>

      {/* Dropdown Menu */}
      <AnimatePresence data-id="element-3856">
        {isOpen && <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: -10
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: -10
      }} transition={{
        duration: 0.15,
        ease: 'easeOut'
      }} className="absolute top-full right-0 mt-2 w-64 bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-50" data-id="element-3857">
            <div className="p-2" data-id="element-3858">
              <div className="px-3 py-2 text-xs font-bold text-slate-400 uppercase tracking-wider" data-id="element-3859">
                Accounts
              </div>

              {accounts.map(account => {
            const isActive = account.id === activeAccountId;
            const AccountIcon = getAccountIcon(account.role);
            const accountColors = getAccountColor(account.role);
            return <button key={account.id} onClick={() => {
              onSwitchAccount(account.id);
              setIsOpen(false);
            }} className={`
                      w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all
                      ${isActive ? `${accountColors.bg} ${accountColors.border} border` : 'hover:bg-slate-50'}
                    `} data-id="element-3860">
                    {/* Avatar */}
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${accountColors.bg}`} data-id="element-3861">
                      <AccountIcon className={`w-5 h-5 ${accountColors.text}`} strokeWidth={2.5} data-id="element-3862" />
                    </div>

                    {/* Name & Role */}
                    <div className="flex-1 text-left min-w-0" data-id="element-3863">
                      <div className="font-bold text-slate-900 truncate" data-id="element-3864">
                        {account.displayName}
                      </div>
                      <div className="text-xs text-slate-500" data-id="element-3865">
                        {getAccountRoleLabel(account.role)}
                      </div>
                    </div>

                    {/* Active Indicator */}
                    {isActive && <motion.div initial={{
                scale: 0
              }} animate={{
                scale: 1
              }} transition={{
                type: 'spring',
                stiffness: 500,
                damping: 25
              }} data-id="element-3866">
                        <Check className={`w-5 h-5 ${accountColors.text}`} strokeWidth={3} data-id="element-3867" />
                      </motion.div>}
                  </button>;
          })}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}